import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './RiskStrip.module.css';
import { UiLink } from './UiLink';

export interface RiskStripItem {
  text: ReactNode;
  /** `to` takes `#section-id` for a row on this page, or an app path. */
  link?: { label: string; to?: string; onClick?: () => void };
  /** Expected to clear with the next submission: listed apart, in neutral colours. */
  waiting?: boolean;
}

export interface RiskStripProps {
  /** Defaults to "Needs attention". */
  title?: string;
  /** Heading of the waiting items. Defaults to "Waiting for the next submission". */
  waitingTitle?: string;
  items: RiskStripItem[];
}

export function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 2 21h20z" />
      <path d="M12 10v5M12 18h.01" />
    </svg>
  );
}

function ItemList({ items, waiting }: { items: RiskStripItem[]; waiting?: boolean }) {
  return (
    <ul className={classes.list}>
      {items.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: items have no id and are rebuilt from the same data
        <li key={i} className={waiting ? `${classes.item} ${classes.waitingItem}` : classes.item}>
          <span className={classes.text}>{item.text}</span>
          {item.link?.to ? (
            <UiLink to={item.link.to} onClick={item.link.onClick}>
              {item.link.label}
            </UiLink>
          ) : item.link ? (
            <button type="button" className={classes.linkButton} onClick={item.link.onClick}>
              {item.link.label}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function RiskStrip({ title, waitingTitle, items }: RiskStripProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const waitingId = useId();
  const risks = items.filter((i) => !i.waiting);
  const waiting = items.filter((i) => i.waiting);

  return (
    <>
      {risks.length > 0 && (
        <section className={classes.strip} aria-labelledby={headingId}>
          <div className={classes.head}>
            <WarningIcon className={classes.icon} />
            <h2 id={headingId} className={classes.title}>
              {title ?? t('ui.needsAttention')}
            </h2>
          </div>
          <ItemList items={risks} />
        </section>
      )}
      {waiting.length > 0 && (
        <section className={`${classes.strip} ${classes.waiting}`} aria-labelledby={waitingId}>
          <div className={classes.head}>
            <h2 id={waitingId} className={classes.title}>
              {waitingTitle ?? t('ui.waitingTitle')}
            </h2>
          </div>
          <ItemList items={waiting} waiting />
        </section>
      )}
    </>
  );
}
